// src/features/system/admin/components/FederatedProvisioningPanel.tsx
//
// SUPERADMIN-ONLY tool to pre-provision federated (Google OIDC) users.
//
// Design rationale (enterprise best practice):
//   • SSO-only — the backend creates a passwordless, locked account. Interactive
//     password login is impossible; Google federation is the sole auth path.
//   • No name field — the user's real name, picture and verified email are
//     auto-fetched from Google OIDC claims on first login (verified-email
//     auto-link). Pre-entering them would only risk drift.
//   • Requests exactly: email · tenant · roles · language.
//   • Requires PAC_IAM_IDENTITY_ALLOW_EMAIL_AUTOLINK=true in the target
//     environment for the verified-email binding to occur on first login.

import React, { useMemo, useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAuth } from '@/shared/auth';
import { useTenants } from '@/features/system/tenants/hooks/useTenants';
import { useDeleteIAMUser, useIAMRoles, useProvisionIAMUser } from '@/features/system/iam/hooks/useIAMUsers';
import { fetchIAMUsers } from '@/features/system/iam/api/iamApi';
import type { IAMUser } from '@/features/system/iam/api/iamApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function FederatedProvisioningPanel() {
  const { capabilities } = useAuth();
  const isSuperadmin = capabilities.isSuperadmin === true;

  useDocumentTitle('Admin', 'IAM', 'Provision');

  const { data: tenants = [] } = useTenants();
  const { data: rolesResp } = useIAMRoles(isSuperadmin);
  const roles = rolesResp?.items ?? [];
  const provision = useProvisionIAMUser();
  const deleteUser = useDeleteIAMUser();

  const [email,      setEmail]      = useState('');
  const [tenantId,   setTenantId]   = useState('');
  const [selRoles,   setSelRoles]   = useState<string[]>([]);
  const [language,   setLanguage]   = useState('en');
  const [sendInvitation, setSendInvitation] = useState(true);
  const [serverError, setServerError] = useState<string | null>(null);
  const [created,     setCreated]     = useState<IAMUser | null>(null);
  const [duplicateUser, setDuplicateUser] = useState<IAMUser | null>(null);
  const [resolvingDuplicate, setResolvingDuplicate] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit  = emailValid && tenantId !== '' && !provision.isPending;

  const selectedTenant = useMemo(
    () => tenants.find(t => String(t.id_tenant) === tenantId),
    [tenants, tenantId],
  );

  const toggleRole = (key: string) =>
    setSelRoles(prev => (prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]));

  const reset = () => {
    setEmail(''); setTenantId(''); setSelRoles([]); setLanguage('en'); setSendInvitation(true);
    setServerError(null); setCreated(null); setDuplicateUser(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setServerError(null);
    setCreated(null);
    setDuplicateUser(null);
    try {
      const user = await provision.mutateAsync({
        email: email.trim().toLowerCase(),
        tenant_id: Number(tenantId),
        roles: selRoles.length ? selRoles : undefined,
        language,
        send_invitation: sendInvitation,
      });
      setCreated(user);
      // Keep tenant/roles/language for rapid batch provisioning; clear email only.
      setEmail('');
    } catch (err: any) {
      const msg = err?.message ?? 'Could not provision user.';
      setServerError(msg);

      if (/already exists/i.test(String(msg))) {
        try {
          setResolvingDuplicate(true);
          const existing = await fetchIAMUsers({
            search: email.trim().toLowerCase(),
            limit: 20,
            offset: 0,
          });
          const found = (existing.items || []).find(
            u => (u.email || '').toLowerCase() === email.trim().toLowerCase()
              || (u.username || '').toLowerCase() === email.trim().toLowerCase(),
          );
          setDuplicateUser(found ?? null);
        } catch {
          setDuplicateUser(null);
        } finally {
          setResolvingDuplicate(false);
        }
      }
    }
  };

  const deleteDuplicate = async () => {
    if (!duplicateUser) return;
    try {
      await deleteUser.mutateAsync(duplicateUser.id_user);
      setServerError('Existing provisioned user removed. You can retry now.');
      setDuplicateUser(null);
    } catch (err: any) {
      setServerError(err?.message ?? 'Could not remove existing user.');
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="pac-glass rounded-lg p-6 text-sm text-neutral-400">
        This tool is restricted to system superadmins.
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      {/* Intro */}
      <div className="pac-glass rounded-lg p-4 space-y-1.5">
        <h2 className="text-base font-semibold text-neutral-100">Provision Federated User</h2>
        <p className="text-sm text-neutral-400">
          Pre-create a Google SSO account before the user&apos;s first login. The account is
          passwordless — name, picture and verified email are fetched from Google automatically
          when they sign in for the first time.
        </p>
      </div>

      <form onSubmit={onSubmit} className="pac-glass rounded-lg p-4 space-y-4">
        {/* Email */}
        <label className="block text-sm text-neutral-300">
          Google Email *
          <input
            autoFocus
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setServerError(null); }}
            placeholder="person@gmail.com"
            className={`mt-1 w-full bg-black border rounded px-3 py-2 focus:outline-none focus:ring-2 ${
              email.length > 0 && !emailValid
                ? 'border-amber-700 ring-amber-500/40 focus:ring-amber-500'
                : 'border-neutral-700 focus:ring-emerald-500'
            }`}
          />
          <span className="text-xs text-neutral-500 mt-1 block">
            Must match the user&apos;s verified Google address exactly — it becomes the login identity.
          </span>
        </label>

        {/* Tenant */}
        <label className="block text-sm text-neutral-300">
          Tenant *
          <select
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2"
          >
            <option value="">— select tenant —</option>
            {tenants.map(t => (
              <option key={t.id_tenant} value={String(t.id_tenant)}>
                {t.display_name ?? t.tenant_name ?? t.code} #{t.id_tenant}
              </option>
            ))}
          </select>
        </label>

        {/* Roles */}
        <div className="space-y-1.5">
          <p className="text-sm text-neutral-300">
            Roles <span className="text-neutral-500">(defaults to <code className="text-neutral-400">user</code> if none selected)</span>
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto border border-neutral-800 rounded-lg p-2">
            {roles.length === 0 && (
              <span className="text-xs text-neutral-500 col-span-full px-1 py-1">No roles available.</span>
            )}
            {roles.map(r => {
              const checked = selRoles.includes(r.role_key);
              return (
                <label
                  key={r.role_key}
                  title={r.description || r.role_key}
                  className={`flex items-center gap-2 text-sm rounded px-2 py-1.5 cursor-pointer select-none transition-colors ${
                    checked ? 'pac-glass-subtle text-emerald-300' : 'text-neutral-300 hover:bg-neutral-800/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleRole(r.role_key)}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <span className="truncate">{r.display_name || r.role_key}</span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-neutral-500">
            Tip: for kiosk / TableOps customers assign{' '}
            <code className="text-neutral-400">tableops.superadmin</code> — it is TableOps-only and
            preserves the <code className="text-neutral-400">/tableops</code> landing redirect.
          </p>
        </div>

        {/* Language */}
        <label className="block text-sm text-neutral-300">
          Language
          <select
            value={language}
            onChange={e => setLanguage(e.target.value)}
            className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </label>

        <label className="flex items-start gap-2 text-sm text-neutral-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sendInvitation}
            onChange={e => setSendInvitation(e.target.checked)}
            className="w-4 h-4 mt-0.5 accent-emerald-500"
          />
          <span>
            Send invitation email now
            <span className="block text-xs text-neutral-500 mt-0.5">
              Delivered via backend comms using sender
              {' '}<span className="text-neutral-400">PACDaemon &lt;admin@pacdaemon.com&gt;</span>
              {' '}unless COMMS_EMAIL_FROM is overridden.
            </span>
          </span>
        </label>

        {/* Success banner */}
        {created && (
          <div className="flex items-start gap-2 bg-emerald-950/50 border border-emerald-800/60 rounded-lg px-3 py-2.5">
            <span className="text-emerald-400 text-sm mt-px">✓</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-emerald-300">User provisioned</p>
              <p className="text-xs text-emerald-400/90 mt-0.5 break-words">
                {created.username} (#{created.id_user}) in tenant {selectedTenant?.display_name ?? selectedTenant?.tenant_name ?? created.tenant_id}.
                {' '}They can now sign in with Google
                {sendInvitation
                  ? (created.invitation_sent
                    ? ', and the invitation email was queued.'
                    : '. Invitation email was requested, but delivery is currently disabled in this environment.')
                  : '.'}
              </p>
            </div>
            <button type="button" onClick={() => setCreated(null)} className="text-emerald-500 hover:text-emerald-300 text-xs mt-0.5">dismiss</button>
          </div>
        )}

        {/* Error banner */}
        {serverError && (
          <div className="flex items-start gap-2 bg-rose-950/60 border border-rose-800/60 rounded-lg px-3 py-2.5">
            <span className="text-rose-400 text-sm mt-px">✕</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-rose-300">Provisioning failed</p>
              <p className="text-xs text-rose-400 mt-0.5 break-words">{serverError}</p>
              {/already exists/i.test(serverError) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <a
                    href="/admin?adminTab=IAM&iamTab=users"
                    className="text-xs px-2 py-1 rounded border border-rose-700 text-rose-300 hover:bg-rose-900/30"
                  >
                    Open Users tab
                  </a>
                  {resolvingDuplicate && (
                    <span className="text-xs text-rose-300/80">Locating existing user…</span>
                  )}
                  {duplicateUser && (
                    <button
                      type="button"
                      onClick={deleteDuplicate}
                      disabled={deleteUser.isPending}
                      className="text-xs px-2 py-1 rounded border border-rose-700 text-rose-300 hover:bg-rose-900/30 disabled:opacity-60"
                    >
                      {deleteUser.isPending
                        ? 'Removing…'
                        : `Remove existing user (#${duplicateUser.id_user})`}
                    </button>
                  )}
                </div>
              )}
            </div>
            <button type="button" onClick={() => setServerError(null)} className="text-rose-500 hover:text-rose-300 text-xs mt-0.5">dismiss</button>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={reset}
            className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 text-sm rounded font-semibold pac-glass-raised text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-emerald-200 transition-colors"
          >
            {provision.isPending ? 'Provisioning…' : 'Provision user'}
          </button>
        </div>
      </form>
    </div>
  );
}
